// Inline-SVG brand-icon set — one mark per brand network (spec §4 / §6.1).
//
// Real brand artwork is allowed here (the one exception to "icons via the
// Material Symbols `Icon`"), but every mark stays MONOCHROME: it draws with
// `fill="currentColor"` so the wrapping button can size and tint it via
// `--brand-*` tokens. NO npm dependency — these are simple-icons-style
// single-path glyphs inlined by hand. Each accepts `className`/`style`/`size`.
//
// `website` has NO entry here — it uses the Material Symbols `language` icon
// (see SocialLinks). Only the 9 brand networks live in this map.

import type { CSSProperties } from "react";
import type { AdSocialNetwork } from "@to/db";

export interface BrandIconProps {
  className?: string;
  style?: CSSProperties;
  /** Square px size applied to both width and height. Defaults to 1em. */
  size?: number;
}

/** The 9 networks that render a real brand mark (everything except `website`). */
export type BrandNetwork = Exclude<AdSocialNetwork, "website">;

type BrandIcon = (props: BrandIconProps) => React.JSX.Element;

/**
 * Shared SVG frame — fixes the viewBox/fill contract so each mark only supplies
 * its glyph path(s). `fill="currentColor"` makes the glyph inherit the button's
 * color (a `--brand-*` token), keeping the mark monochrome.
 */
function svgProps({ className, style, size }: BrandIconProps) {
  return {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    width: size ?? "1em",
    height: size ?? "1em",
    className,
    style,
    "aria-hidden": true,
    focusable: false as const,
  };
}

const FacebookIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const TikTokIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const YouTubeIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const LinkedInIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const XTwitterIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Official simple-icons `zillow` path (24×24), inlined verbatim.
const ZillowIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path d="M12.006 0L1.086 8.627v3.868c3.386-2.013 11.219-5.13 14.763-6.015.11-.024.16.005.227.078.372.427 1.586 1.899 1.916 2.301a.128.128 0 0 1-.03.195 43.607 43.607 0 0 0-6.67 6.527c-.03.037-.006.043.012.03 2.642-1.134 8.828-2.94 11.622-3.452V8.627zm-.48 11.177c-2.136.708-8.195 3.307-10.452 4.576V24h21.852v-7.936c-2.99.506-11.902 3.16-15.959 5.246a.183.183 0 0 1-.23-.036l-2.044-2.429c-.055-.061-.062-.098.011-.208 1.574-2.3 4.789-5.899 6.833-7.418.042-.03.031-.06-.012-.042Z" />
  </svg>
);

// Realtor.com is NOT in simple-icons. This path is a contour trace of the
// official mark supplied on TRU-36 (house silhouette with the serif "r"
// knocked out), so it follows the real artwork rather than approximating it.
// `fillRule="evenodd"` is what turns the second subpath into the knockout.
const RealtorIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path
      fillRule="evenodd"
      d="M11.8 0.3L12 0.3L12.91 1.31L13.11 1.31L13.11 1.51L13.61 2.02L13.82 2.02L13.82 2.22L14.42 2.82L14.62 2.82L14.62 3.03L15.13 3.53L15.33 3.53L15.33 3.73L15.73 3.93L15.73 4.13L16.03 4.24L16.13 4.54L16.34 4.54L24 12.2L20.07 12.3L20.07 23.6L3.33 23.7L3.33 12.2L0 12.2L0 12L2.82 9.18L3.03 9.18L3.03 8.97L3.43 8.77L3.43 8.57L3.63 8.57L3.63 8.37L3.93 8.27L4.13 7.87L4.34 7.87ZM15.53 7.16L14.12 7.56L14.02 7.76L13.41 7.97L12.91 8.47L12.71 8.47L11.8 9.48L11.39 10.29L11.19 7.56L6.05 7.56L6.05 7.87L6.76 7.97L7.16 8.37L7.36 8.37L7.66 8.97L7.76 19.26L7.66 20.47L7.36 21.38L6.35 21.98L5.95 21.98L5.85 22.39L13.21 22.39L13.11 21.98L12.71 21.98L12.5 21.78L12 21.68L11.6 21.28L11.39 20.77L11.29 13.31L11.6 12L12.3 10.29L12.5 10.18L12.71 9.68L13.71 8.87L14.12 8.97L14.62 10.49L15.43 11.29L16.84 11.39L17.34 11.19L17.95 10.59L18.15 9.98L18.15 8.87L17.85 8.07L17.34 7.56L16.74 7.26Z"
    />
  </svg>
);

/**
 * NAR membership mark — the block "R", traced from the official asset on
 * TRU-36. NOT a social network (it has no `BRAND_ICONS` entry); it lives here
 * because this file is the one sanctioned home for brand artwork. The
 * "REALTOR®" wordmark under the block in the source asset is deliberately
 * dropped: it is illegible at the ~20px this renders at, and the block alone
 * is the recognisable mark.
 */
export const NarLogoIcon: BrandIcon = (props) => (
  <svg {...svgProps(props)}>
    <path
      fillRule="evenodd"
      d="M0.8 0L23.2 0L23.2 24L0.8 24ZM4.03 3.19L3.98 20.66L10.36 20.66L10.36 3.19ZM12.82 3.19L12.77 11.92L16.16 11.87L17.45 11.51L18.22 11.1L18.58 10.74L18.68 10.74L19.3 10.02L19.61 9.4L19.86 8.48L19.91 7.45L19.81 6.68L19.55 5.86L19.3 5.34L18.99 4.99L18.99 4.88L17.91 3.91L17.7 3.85L17.65 3.75L17.34 3.6L17.19 3.6L16.63 3.34L15.85 3.19ZM12.82 12.08L12.77 20.71L21.46 20.71Z"
    />
  </svg>
);

/** Brand mark per network. `website` is intentionally absent (uses `Icon`). */
export const BRAND_ICONS: Record<BrandNetwork, BrandIcon> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
  linkedin: LinkedInIcon,
  x_twitter: XTwitterIcon,
  zillow: ZillowIcon,
  realtor_com: RealtorIcon,
};
